/**
 * FHIR R4 type definitions used by the Care Pathway Studio Aidbox client
 * (PROD-642 Sprint 2.2).
 *
 * The shapes here are deliberately a minimal subset of the canonical FHIR
 * R4 spec - only the resources and fields the Pathway Studio reads or
 * writes. Aidbox accepts the full FHIR JSON envelope, so any field this
 * module does not name is preserved on round-trip via the `extension`,
 * `contained`, or per-resource passthrough channels (each resource carries
 * `[k: string]: unknown` so the client never silently drops an unknown
 * field on the response side).
 *
 * Sources of truth:
 *   - FHIR R4 ResourceType definitions
 *     (https://hl7.org/fhir/R4/resourcelist.html)
 *   - Confluence ENG/4297621505 "Aidbox FHIR Schema quirks for CPS / PA / PM"
 *     (HTTP 410 quirk + empty-action rejection)
 *   - OneDrive `ehr-to-pathway-studio-integration-spec.md` §3.2
 *
 * HC5: zero em / en dashes anywhere. Section separators use hyphens or
 * commas.
 */

/** Tenant isolation extension URL per CPS / PA / PM convention. */
export const TENANT_EXTENSION_URL =
  'http://scrips.com/fhir/StructureDefinition/tenant-id';

/** Risk-tier extension URL for ActivityDefinition resources (ADR-001). */
export const RISK_TIER_EXTENSION_URL =
  'http://scrips.com/fhir/StructureDefinition/risk-tier';

/** Generic FHIR R4 Extension union accepted by every resource. */
export interface FhirExtension {
  url: string;
  valueString?: string;
  valueBoolean?: boolean;
  valueInteger?: number;
  valueDecimal?: number;
  valueCode?: string;
  valueUri?: string;
  valueDateTime?: string;
  valueReference?: { reference: string };
  valueCodeableConcept?: CodeableConcept;
  extension?: FhirExtension[];
}

export interface Coding {
  system?: string;
  code?: string;
  display?: string;
  version?: string;
}

export interface CodeableConcept {
  coding?: Coding[];
  text?: string;
}

export interface Identifier {
  system?: string;
  value?: string;
  use?: string;
}

export interface Reference {
  reference?: string;
  display?: string;
  type?: string;
  identifier?: Identifier;
}

export interface Period {
  start?: string;
  end?: string;
}

export interface Meta {
  versionId?: string;
  lastUpdated?: string;
  source?: string;
  profile?: string[];
  tag?: Coding[];
}

/** Resource types the CPS client reads or writes. */
export type CpsResourceType =
  | 'PlanDefinition'
  | 'ActivityDefinition'
  | 'Questionnaire'
  | 'Library'
  | 'CarePlan'
  | 'Task'
  | 'QuestionnaireResponse'
  | 'Observation'
  | 'Provenance'
  | 'AuditEvent';

/** Base shape every FHIR resource shares (subset). */
export interface FhirResource {
  resourceType: CpsResourceType;
  id?: string;
  meta?: Meta;
  extension?: FhirExtension[];
  /**
   * Open passthrough so the client never drops fields the spec does not
   * explicitly model. Important: callers must not write through this
   * field - use the typed properties above.
   */
  [key: string]: unknown;
}

/** PlanDefinition.action subset. Aidbox rejects empty arrays here. */
export interface PlanDefinitionAction {
  title?: string;
  description?: string;
  textEquivalent?: string;
  code?: CodeableConcept[];
  definitionCanonical?: string;
  triggerDefinition?: unknown;
  condition?: unknown[];
  action?: PlanDefinitionAction[];
}

export interface PlanDefinition extends FhirResource {
  resourceType: 'PlanDefinition';
  url?: string;
  identifier?: Identifier[];
  version?: string;
  name?: string;
  title?: string;
  status: 'draft' | 'active' | 'retired' | 'unknown';
  experimental?: boolean;
  date?: string;
  publisher?: string;
  description?: string;
  type?: CodeableConcept;
  /**
   * Aidbox FHIR Schema quirk: an empty `action` array is rejected with
   * a 422. The client guards against this on every PlanDefinition write
   * (see `assertPlanDefinitionWritable`).
   */
  action?: PlanDefinitionAction[];
}

export interface ActivityDefinition extends FhirResource {
  resourceType: 'ActivityDefinition';
  url?: string;
  identifier?: Identifier[];
  version?: string;
  name?: string;
  title?: string;
  status: 'draft' | 'active' | 'retired' | 'unknown';
  description?: string;
  kind?: string;
  code?: CodeableConcept;
}

export interface QuestionnaireItem {
  linkId: string;
  text?: string;
  type:
    | 'group'
    | 'display'
    | 'boolean'
    | 'decimal'
    | 'integer'
    | 'string'
    | 'text'
    | 'choice'
    | 'open-choice'
    | 'date'
    | 'dateTime'
    | 'time'
    | 'quantity';
  required?: boolean;
  repeats?: boolean;
  code?: Coding[];
  item?: QuestionnaireItem[];
}

export interface Questionnaire extends FhirResource {
  resourceType: 'Questionnaire';
  url?: string;
  identifier?: Identifier[];
  version?: string;
  name?: string;
  title?: string;
  status: 'draft' | 'active' | 'retired' | 'unknown';
  code?: Coding[];
  item?: QuestionnaireItem[];
}

export interface Library extends FhirResource {
  resourceType: 'Library';
  url?: string;
  identifier?: Identifier[];
  version?: string;
  name?: string;
  title?: string;
  status: 'draft' | 'active' | 'retired' | 'unknown';
  type: CodeableConcept;
  description?: string;
}

export interface CarePlan extends FhirResource {
  resourceType: 'CarePlan';
  identifier?: Identifier[];
  status:
    | 'draft'
    | 'active'
    | 'on-hold'
    | 'revoked'
    | 'completed'
    | 'entered-in-error'
    | 'unknown';
  intent: 'proposal' | 'plan' | 'order' | 'option';
  subject: Reference;
  period?: Period;
  instantiatesCanonical?: string[];
}

export interface Task extends FhirResource {
  resourceType: 'Task';
  identifier?: Identifier[];
  status:
    | 'draft'
    | 'requested'
    | 'received'
    | 'accepted'
    | 'rejected'
    | 'ready'
    | 'cancelled'
    | 'in-progress'
    | 'on-hold'
    | 'failed'
    | 'completed'
    | 'entered-in-error';
  intent: string;
  for?: Reference;
  description?: string;
}

export interface QuestionnaireResponseItemAnswer {
  valueBoolean?: boolean;
  valueDecimal?: number;
  valueInteger?: number;
  valueString?: string;
  valueCoding?: Coding;
}

export interface QuestionnaireResponseItem {
  linkId: string;
  text?: string;
  answer?: QuestionnaireResponseItemAnswer[];
  item?: QuestionnaireResponseItem[];
}

export interface QuestionnaireResponse extends FhirResource {
  resourceType: 'QuestionnaireResponse';
  identifier?: Identifier;
  questionnaire?: string;
  status:
    | 'in-progress'
    | 'completed'
    | 'amended'
    | 'entered-in-error'
    | 'stopped';
  subject?: Reference;
  authored?: string;
  author?: Reference;
  item?: QuestionnaireResponseItem[];
}

export interface Observation extends FhirResource {
  resourceType: 'Observation';
  identifier?: Identifier[];
  status:
    | 'registered'
    | 'preliminary'
    | 'final'
    | 'amended'
    | 'corrected'
    | 'cancelled'
    | 'entered-in-error'
    | 'unknown';
  code: CodeableConcept;
  subject?: Reference;
  effectiveDateTime?: string;
  valueQuantity?: { value: number; unit?: string; system?: string; code?: string };
  valueString?: string;
  valueBoolean?: boolean;
  valueInteger?: number;
  valueCodeableConcept?: CodeableConcept;
  derivedFrom?: Reference[];
}

export interface ProvenanceAgent {
  type?: CodeableConcept;
  who: Reference;
  onBehalfOf?: Reference;
}

export interface Provenance extends FhirResource {
  resourceType: 'Provenance';
  target: Reference[];
  recorded: string;
  reason?: CodeableConcept[];
  activity?: CodeableConcept;
  agent: ProvenanceAgent[];
}

export interface AuditEventAgent {
  type?: CodeableConcept;
  who?: Reference;
  requestor: boolean;
}

export interface AuditEventEntity {
  what?: Reference;
  type?: Coding;
  role?: Coding;
  name?: string;
  description?: string;
}

export interface AuditEvent extends FhirResource {
  resourceType: 'AuditEvent';
  type: Coding;
  subtype?: Coding[];
  action?: 'C' | 'R' | 'U' | 'D' | 'E';
  recorded: string;
  outcome?: '0' | '4' | '8' | '12';
  agent: AuditEventAgent[];
  source: { observer: Reference; site?: string };
  entity?: AuditEventEntity[];
}

/** FHIR Bundle subset used by search responses. */
export interface BundleEntry<T extends FhirResource> {
  fullUrl?: string;
  resource?: T;
  search?: { mode?: 'match' | 'include' | 'outcome'; score?: number };
}

export interface Bundle<T extends FhirResource> {
  resourceType: 'Bundle';
  type:
    | 'document'
    | 'message'
    | 'transaction'
    | 'transaction-response'
    | 'batch'
    | 'batch-response'
    | 'history'
    | 'searchset'
    | 'collection';
  total?: number;
  link?: { relation: string; url: string }[];
  entry?: BundleEntry<T>[];
}

/** Search params accepted by `searchPlanDefinitions` and friends. */
export type SearchParams = Record<string, string | number | boolean | undefined>;
